import {
  Box,
  Popover,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { RootState } from '../../store'
import { createSelector } from '@reduxjs/toolkit'
import { SketchPicker } from 'react-color'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateGridSettings } from '../../actions/construction'

interface GridSettingsProps {}

const gridSettingsSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.gridSettings,
)

const GridSettingsComponent: React.FC<GridSettingsProps> = () => {
  const dispatch = useDispatch()
  const gridSettings = useSelector(gridSettingsSelector)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleTextChange = (e: any) => {
    const value = e.target.value
    if (/^#([0-9A-F]{3}){1,2}$/i.test(value)) {
      dispatch(updateGridSettings({ color: value }))
    }
  }

  const handleBoxClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleColorChange = (newColor: any) => {
    dispatch(updateGridSettings({ color: newColor.hex }))
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <Stack
      gap='0.75rem'
      sx={{
        p: '0.75rem',
        width: '10rem',
        borderRadius: '0.5rem',
        background:
          'radial-gradient(50% 73.19% at 50% 50%, rgba(88, 88, 88, 0.3) 0%, rgba(204, 204, 204, 0.3) 100%)',
      }}
    >
      <Typography
        sx={{
          color: '#06004A',
          fontSize: '0.8rem',
          fontWeight: 500,
          lineHeight: 'normal',
          borderBottom: '1px solid #97999B',
          pb: '0.25rem',
        }}
      >
        Grid Settings
      </Typography>
      <Box display='flex' flexDirection='column' gap='0.3rem'>
        <Typography
          sx={{
            color: '#06004A',
            fontSize: '0.76rem',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Grid Color
        </Typography>
        <Box sx={{ display: 'flex', position: 'relative' }}>
          <TextField
            value={gridSettings.color}
            onChange={handleTextChange}
            sx={{
              borderRadius: '0.3rem',
              background: '#ffffff',
              padding: '0.3rem 0.6rem',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none',
                },
                '& input': {
                  fontSize: '0.76rem',
                  color: '#1D1C1F',
                  lineHeight: 'normal',
                  padding: '0',
                },
              },
            }}
          ></TextField>
          <Box
            onClick={handleBoxClick}
            sx={{
              width: '1rem',
              height: '1rem',
              bgcolor: gridSettings.color,
              border: '1px solid #ccc',
              borderRadius: '0.3rem',
              cursor: 'pointer',
              position: 'absolute',
              right: '0.5rem',
              top: '0.3rem',
            }}
          />
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                style: {
                  boxShadow: '0px 4px 4px 0px #00000040',
                  marginTop: '-0.5rem',
                  borderRadius: '0.4rem',
                },
              },
            }}
          >
            <SketchPicker
              color={gridSettings.color}
              onChange={handleColorChange}
            />
          </Popover>
        </Box>
      </Box>
      <Box display='flex' flexDirection='column' gap='0.3rem'>
        <Typography
          sx={{
            color: '#06004A',
            fontSize: '0.76rem',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Cell Size
        </Typography>
        <Slider
          aria-label='CellSize'
          defaultValue={gridSettings.cellSize}
          valueLabelDisplay='auto'
          step={1}
          marks
          min={1}
          max={10}
          onChangeCommitted={(_, value) =>
            dispatch(updateGridSettings({ cellSize: value }))
          }
        />
      </Box>
      <Box display='flex' flexDirection='column' gap='0.3rem'>
        <Typography
          sx={{
            color: '#06004A',
            fontSize: '0.76rem',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Cell Frequency
        </Typography>
        <Slider
          aria-label='CellFrequency'
          defaultValue={gridSettings.cellFrequency}
          valueLabelDisplay='auto'
          step={2}
          marks
          min={2}
          max={20}
          onChangeCommitted={(_, value) =>
            dispatch(updateGridSettings({ cellFrequency: value }))
          }
        />
      </Box>
      <Box display='flex' flexDirection='column' gap='0.3rem'>
        <Typography
          sx={{
            color: '#06004A',
            fontSize: '0.76rem',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Snap to grid
        </Typography>
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={gridSettings.snapToGrid}
          exclusive
          aria-label='SnapToGrid'
          sx={{
            flex: 1,
            '.MuiToggleButton-root': {
              margin: 0,
              border: 'none',
              borderRadius: '0.3rem',
              cursor: 'pointer',
            },
          }}
        >
          {[true, false].map((snap, index) => (
            <ToggleButton
              disableRipple
              key={index}
              value={snap}
              onClick={() => dispatch(updateGridSettings({ snapToGrid: snap }))}
              sx={{
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#04002E',
                textTransform: 'none',
                cursor: 'pointer',
                padding: '0.3rem 0.6rem',
                fontSize: '0.76rem',
                '&:hover': {
                  backgroundColor: '#ffffff',
                  color: '#04002E',
                  cursor: 'pointer',
                },
                '&.Mui-selected': {
                  backgroundColor: '#1705E5',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#1705E5',
                    color: '#ffffff',
                  },
                },
              }}
            >
              {snap ? 'Yes' : 'No'}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Stack>
  )
}

export default GridSettingsComponent
